import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Button,
} from '@mui/material';
import BuySellAccountPicker from './BuySellAccountPicker.jsx';
import BuySellDafUpload from './BuySellDafUpload.jsx';
import { getTodayIsoDate, isDafFileTooLarge, MAX_DAF_MB } from './buySellUtils.js';
import useBuySellAccountSearch from './useBuySellAccountSearch.js';

const BuySellEditDialog = ({ open, row, onClose, onSave, stageOptions, onSearch }) => {
  const [accountName, setAccountName] = useState('');
  const [buySellAccountId, setBuySellAccountId] = useState('');
  const [bac, setBac] = useState('');
  const [purchasingBAC, setPurchasingBAC] = useState('');
  const [stageDate, setStageDate] = useState('');
  const [stage, setStage] = useState('DAF');
  const [notes, setNotes] = useState('');
  const [dafFile, setDafFile] = useState(null);
  const [dafError, setDafError] = useState('');
  const [existingDafName, setExistingDafName] = useState('');
  const purchasingSearch = useBuySellAccountSearch({ open, onSearch });
  const { resetSearch: resetPurchasingSearch, selectedAccount: selectedPurchasingAccount } = purchasingSearch;

  const isDafStage = stage === 'DAF';

  useEffect(() => {
    if (!row) return;
    setAccountName(row.accountName || '');
    setBuySellAccountId(row.buySellAccountId || '');
    setBac(row.bac || '');
    setPurchasingBAC(row.purchasingBAC || '');
    setStageDate(row.stageDate || getTodayIsoDate());
    setStage(row.buySellStage || 'DAF');
    setNotes(row.notes || '');
    setDafFile(null);
    setDafError('');
    setExistingDafName(row?.dafAttachment?.fileName || (row?.hasDafAttachment ? 'DAF.pdf' : ''));
    resetPurchasingSearch();
  }, [resetPurchasingSearch, row]);

  useEffect(() => {
    if (!isDafStage) {
      setDafFile(null);
      setDafError('');
    }
  }, [isDafStage]);

  useEffect(() => {
    if (!selectedPurchasingAccount) return;
    setBuySellAccountId(selectedPurchasingAccount.accountId || '');
    if (String(selectedPurchasingAccount.bac || '').trim()) {
      setPurchasingBAC(selectedPurchasingAccount.bac);
    }
  }, [selectedPurchasingAccount]);

  const handlePurchasingAccountChange = (next) => {
    purchasingSearch.handleAccountChange(next);
  };

  const canSave = useMemo(() => {
    if (!accountName.trim()) return false;
    if (!bac.trim() && !purchasingBAC.trim()) return false;
    if (!stageDate) return false;
    if (!stage) return false;
    return true;
  }, [accountName, bac, purchasingBAC, stage, stageDate]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setDafFile(null);
      setDafError('');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      setDafFile(null);
      setDafError('Only PDF uploads are supported.');
      return;
    }
    if (isDafFileTooLarge(file)) {
      setDafFile(null);
      setDafError(`File exceeds ${MAX_DAF_MB} MB.`);
      return;
    }
    setDafFile(file);
    setDafError('');
  };

  const handleSave = () => {
    if (!row || !canSave) return;
    onSave?.({
      ...row,
      accountName: accountName.trim(),
      buySellAccountId: buySellAccountId.trim(),
      bac: bac.trim(),
      purchasingBAC: purchasingBAC.trim(),
      buySellStage: stage,
      stageDate,
      notes: notes.trim(),
      dafFile,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Buy/Sell</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Account name"
            value={accountName}
            onChange={(event) => setAccountName(event.target.value)}
          />
          <TextField
            fullWidth
            label="Purchasing Salesforce Account ID"
            value={buySellAccountId}
            onChange={(event) => setBuySellAccountId(event.target.value)}
          />
          <BuySellAccountPicker
            label="Search purchasing account"
            placeholder="Example: Summit Chevy South"
            helperText="Selecting an account sets the Purchasing Salesforce Account ID."
            searchOptions={purchasingSearch.searchOptions}
            searchLoading={purchasingSearch.searchLoading}
            inputValue={purchasingSearch.inputValue}
            onInputValueChange={purchasingSearch.setInputValue}
            onSearchQueryChange={purchasingSearch.setSearchQuery}
            selectedAccount={purchasingSearch.selectedAccount}
            onAccountChange={handlePurchasingAccountChange}
          />
          <TextField
            fullWidth
            label="Selling Salesforce Account ID"
            value={row?.accountId || ''}
            InputProps={{ readOnly: true }}
          />
          <TextField
            fullWidth
            label="BAC"
            value={bac}
            onChange={(event) => setBac(event.target.value)}
          />
          <TextField
            fullWidth
            label="Purchasing BAC"
            value={purchasingBAC}
            onChange={(event) => setPurchasingBAC(event.target.value)}
          />
          <TextField
            fullWidth
            label="Stage date"
            type="date"
            value={stageDate}
            onChange={(event) => setStageDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth>
            <InputLabel id="buy-sell-edit-stage-label">Stage</InputLabel>
            <Select
              labelId="buy-sell-edit-stage-label"
              label="Stage"
              value={stage}
              onChange={(event) => setStage(event.target.value)}
            >
              {stageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Notes"
            multiline
            minRows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            helperText="Optional. Shown as SPECIAL NOTES in the email when provided."
          />

          <BuySellDafUpload
            show
            description={isDafStage ? 'Required while in DAF stage.' : 'Optional upload for reference.'}
            dafFile={dafFile}
            existingFileName={existingDafName}
            error={dafError}
            onFileChange={handleFileChange}
            onClearFile={() => setDafFile(null)}
            missingWarning={isDafStage && !dafFile && !existingDafName && !dafError}
            missingWarningText="DAF is missing; email will send with a warning."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuySellEditDialog;
